import {
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Badge,
	Tooltip,
} from '@mui/material';
import { Link, useLocation, useParams } from 'react-router';
import {
	ViewKanbanOutlined as ViewKanbanIcon,
	BusinessCenterOutlined as BusinessCenterIcon,
	VisibilityOutlined as VisibilityIcon,
	ChatBubbleOutline as ChatIcon,
	SettingsOutlined as SettingsIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * Navigation menu for the Investing app
 * @param {Object} props - Component props
 * @param {Object} props.user - The current user object
 * @param {boolean} props.isCollapsed - Whether the sidebar is collapsed
 */
const InvestingNavigation = ({ user, isCollapsed }) => {
	const theme = useTheme();
	const { tenantId } = useParams();
	const { pathname } = useLocation();

	// Generate menu items for Investing app
	const menuItems = () => {
		let items = [
			{
				name: 'Watchlist',
				icon: <VisibilityIcon />,
				component: 'investing-monitors',
				path: `/tenants/${tenantId}/apps/investing/monitors`,
			},
			{
				name: 'Pipeline',
				icon: <ViewKanbanIcon />,
				component: 'investing-pipeline',
				path: `/tenants/${tenantId}/apps/investing/pipeline`,
			},
			{
				name: 'Deals',
				icon: <BusinessCenterIcon />,
				component: 'investing-deals',
				path: `/tenants/${tenantId}/apps/investing/deals`,
			},
			{
				name: 'Prompts',
				icon: <ChatIcon />,
				component: 'investing-prompts',
				path: `/tenants/${tenantId}/apps/investing/prompts`,
			},
		];

		// Only add the settings option for appropriate user roles
		if (user.role === 'admin' || user.role === 'altvia_admin') {
			items.push({
				name: 'Settings',
				icon: <SettingsIcon />,
				component: 'investing-settings',
				path: `/tenants/${tenantId}/apps/investing/settings`,
			});
		}

		return items;
	};

	// Determine selected menu item
	const findSelectedItem = () => {
		const menuItemsOutput = menuItems();
		const selectedItem = menuItemsOutput.find((item) =>
			pathname.includes(item.path)
		);
		return selectedItem ? selectedItem.component : 'investing-pipeline';
	};

	const selected = findSelectedItem();

	return (
		<List>
			{menuItems().map((item) => (
				<Tooltip
					title={isCollapsed ? item.name : ''}
					placement='right'
					key={`tooltip-${item.component}`}
				>
					<ListItemButton
						key={item.component}
						selected={selected === item.component}
						component={Link}
						to={item.path}
						sx={{
							justifyContent: isCollapsed ? 'center' : 'flex-start',
							py: isCollapsed ? 1.5 : 1,
							minHeight: 48,
							'&.Mui-selected': {
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.primary.contrastText,
								'&:hover': {
									backgroundColor: theme.palette.primary.main,
								},
								'& .MuiListItemIcon-root': {
									color: theme.palette.primary.contrastText,
								},
								'& .MuiListItemText-primary': {
									color: theme.palette.primary.contrastText,
								},
							},
							'& .MuiListItemIcon-root': {
								color:
									selected === item.component
										? theme.palette.primary.contrastText
										: theme.palette.primary.main,
								minWidth: isCollapsed ? 0 : 24,
								mr: isCollapsed ? 0 : 2,
							},
						}}
					>
						<ListItemIcon>{item.icon}</ListItemIcon>
						{!isCollapsed && (
							<ListItemText
								primary={item.name}
								sx={{
									color:
										selected === item.component
											? theme.palette.primary.contrastText
											: theme.palette.text.primary,
								}}
							/>
						)}
						{!isCollapsed && item.badge > 0 && (
							<Badge badgeContent={item.badge} color='error' />
						)}
					</ListItemButton>
				</Tooltip>
			))}
		</List>
	);
};

export default InvestingNavigation;
